import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  approvePurchasePlan,
  cancelPurchasePlan,
  createPurchasePlan,
  deletePurchasePlan,
  deletePurchasePlanItem,
  getPurchasePlanGoods,
  submitPurchasePlan,
  unapprovePurchasePlan,
  updatePurchasePlanItem,
  type PurchasePlanGoodsRow,
  type PurchasePlanItemGroup,
  type PurchasePlanRecord,
  type PurchaseSettingItem,
} from '@/api/purchase';
import { getCategoryList, type CategoryListItem } from '@/api/category';
import {
  SpecCell,
  UNLIMITED_SPEC_LABEL,
} from '@/pages/purchase-plan/components/UniqueSpecOrderItemsTable';
import ProductSelector, { type SelectableProduct } from '@/pages/order/components/ProductSelector';
import PasteCodesModal, {
  pasteLineToOrderItem,
  type PasteCodeLine,
  type PasteMissedItem,
} from '@/pages/proxy-order/components/PasteCodesModal';
import { getUser, isDivideAccount, isPurchasePlanManager } from '@/utils/auth';
import { useMember } from '@/hooks/useMember';
import {
  clearDynamicBreadcrumbTitle,
  setDynamicBreadcrumbTitle,
} from '@/hooks/useDynamicBreadcrumb';
import type { ApprovalStep } from '@/types/order';
import MonthPicker from '@/components/base/MonthPicker';
import SearchableSelect from '@/components/base/SearchableSelect';
import ApproveModal from './components/ApproveModal';
import CancelModal from './components/CancelModal';
import ConfirmModal from './components/ConfirmModal';
import HospitalPlanItemsModal from './components/HospitalPlanItemsModal';

type DotTone = 'done' | 'rejected' | 'current' | 'pending';

interface ApprovalNode {
  id: string;
  title: string;
  subtitle?: string;
  statusText?: string;
  statusTone?: DotTone;
  time?: string;
  comment?: string;
  dot: DotTone;
}

function mapApproveState(state: number | string | undefined): ApprovalStep['status'] {
  const n = Number(state);
  if (n === 1) return 'approved';
  if (n === 2) return 'rejected';
  return 'pending';
}

function mapInstanToSteps(instan: PurchasePlanRecord['instan']): ApprovalStep[] {
  if (!Array.isArray(instan) || instan.length === 0) return [];
  return instan
    .filter((flow) => {
      const sort = Number(flow.sort ?? 0);
      const nowSort = Number(flow.now_step_sort ?? 0);
      const isOver = Number(flow.is_over ?? 0);
      if (!flow.now_step_sort && flow.now_step_sort !== 0) return true;
      return sort <= nowSort || isOver !== 1;
    })
    .map((flow, index) => {
      const status = mapApproveState(flow.approve_state);
      const stepName = String(flow.step_name || flow.step || `第${index + 1}级审批`);
      const approverName =
        status === 'pending'
          ? String(flow.account_names || flow.account_name || '待定')
          : String(flow.account_name || flow.account_names || '');
      return {
        id: String(flow.id ?? `flow-${index}`),
        level: Number(flow.sort ?? index + 1) || index + 1,
        approverName,
        role: stepName,
        status,
        approvedAt: flow.approve_time ? String(flow.approve_time) : undefined,
        comment: flow.approve ? String(flow.approve) : undefined,
      };
    });
}

function getPurchaseStatusTag(status: number | string | undefined, statusText?: string) {
  const n = Number(status);
  const label = statusText || '-';
  if (n === 10) return { color: '#19be6b', label, icon: 'ri-checkbox-circle-line' };
  if (n === 1) return { color: '#2d8cf0', label, icon: 'ri-loader-4-line' };
  if (n === 2) return { color: '#ed4014', label, icon: 'ri-close-circle-line' };
  if (n === 99) return { color: '#c5c8ce', label, icon: 'ri-close-line' };
  return { color: '#33bcb7', label, icon: 'ri-time-line' };
}

function buildPurchaseApprovalNodes(row: PurchasePlanRecord): ApprovalNode[] {
  const status = Number(row.status);
  const inApproval = status === 0 || status === 1;
  const isRejected = status === 2;
  const isCancelled = status === 99;
  const steps = mapInstanToSteps(row.instan);
  const submitter = row.member?.nickname || row.member?.account || undefined;

  const nodes: ApprovalNode[] = [
    {
      id: 'submit',
      title: '提交采购计划',
      subtitle: submitter,
      time: row.created_at,
      dot: 'done',
    },
  ];

  if (isCancelled) {
    nodes.push({
      id: 'cancelled',
      title: '计划已取消',
      subtitle: submitter,
      statusText: '已取消',
      statusTone: 'rejected',
      dot: 'rejected',
    });
    return nodes;
  }

  if (steps.length > 0) {
    let currentMarked = false;
    steps.forEach((step) => {
      let dot: DotTone;
      let statusText: string;
      let subtitle: string | undefined;

      if (step.status === 'approved') {
        dot = 'done';
        statusText = '已审批';
        subtitle = step.approverName ? `${step.approverName}（同意）` : undefined;
      } else if (step.status === 'rejected') {
        dot = 'rejected';
        statusText = '已拒绝';
        subtitle = step.approverName ? `${step.approverName}（拒绝）` : undefined;
      } else if (!currentMarked && inApproval) {
        dot = 'current';
        statusText = '待审批';
        subtitle = step.approverName ? `等待 ${step.approverName} 审批` : undefined;
        currentMarked = true;
      } else {
        dot = 'pending';
        statusText = '待审批';
        subtitle = step.approverName ? `等待 ${step.approverName} 审批` : undefined;
      }

      nodes.push({
        id: step.id || `appr-${step.level}`,
        title: step.role,
        subtitle,
        statusText,
        statusTone: dot,
        time: step.approvedAt,
        comment: step.comment,
        dot,
      });
    });
  } else if (!isRejected) {
    nodes.push({
      id: 'approved',
      title: status === 10 ? '审批通过' : '待审批',
      statusText: status === 10 ? '已通过' : '待审批',
      statusTone: status === 10 ? 'done' : inApproval ? 'current' : 'pending',
      dot: status === 10 ? 'done' : inApproval ? 'current' : 'pending',
    });
  }

  return nodes;
}

function accountIdsInclude(
  accountIds: string | null | undefined,
  userId: string | number | null | undefined,
) {
  if (userId == null || userId === '') return false;
  const uid = String(userId).trim();
  if (!uid) return false;
  return String(accountIds || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .some((id) => id === uid);
}

function getCurrentApproveStep(row: PurchasePlanRecord) {
  const instan = row.instan;
  if (!Array.isArray(instan) || instan.length === 0) return null;
  const bySort = instan.find((f) => Number(f.sort) === Number(f.now_step_sort));
  if (bySort) return bySort;
  return instan.find((f) => Number(f.approve_state) === 0) || null;
}

function resolveCanApprove(row: PurchasePlanRecord) {
  const status = Number(row.status);
  if (status !== 0 && status !== 1) return false;
  const current = getCurrentApproveStep(row);
  if (!current || Number(current.approve_state) !== 0) return false;
  return accountIdsInclude(current.account_ids, getUser()?.id);
}

function resolveCanCancel(row: PurchasePlanRecord) {
  const status = Number(row.status);
  return status === 0 || status === 1;
}

/** 已取消可删除；采购计划管理员在可编辑状态（未处理/审批中）也可删除 */
function resolveCanDelete(row: PurchasePlanRecord) {
  const status = Number(row.status);
  if (status === 99) return true;
  if ((status === 0 || status === 1) && isPurchasePlanManager()) return true;
  return false;
}

/**
 * 反审权限：须已通过，且当前账号属于最近一次已通过步骤的审批人
 */
function resolveCanReverse(row: PurchasePlanRecord) {
  if (Number(row.status) !== 10) return false;
  const instan = Array.isArray(row.instan) ? row.instan : [];
  const lastPassed = [...instan]
    .filter((f) => Number(f.approve_state) === 1)
    .sort((a, b) => Number(b.sort ?? 0) - Number(a.sort ?? 0))[0];
  if (!lastPassed) return false;
  return accountIdsInclude(lastPassed.account_ids, getUser()?.id);
}

/** 审批中：本科室提交人或任一审批人可覆盖上传清单 */
function resolveCanEdit(row: PurchasePlanRecord) {
  const status = Number(row.status);
  if (status !== 0 && status !== 1) return false;
  const user = getUser();
  const uid = user?.id;
  if (uid == null || uid === '') return false;
  if (!isDivideAccount()) {
    const userDept = user?.department_id ?? user?.department?.id;
    const rowDept = row.office?.id;
    if (userDept != null && rowDept != null && String(userDept) !== String(rowDept)) {
      return false;
    }
  }
  if (row.account_id != null && String(row.account_id) === String(uid)) return true;
  return (row.instan || []).some((step) => accountIdsInclude(step.account_ids, uid));
}

function applyMonthToValue(month?: string | number) {
  const s = String(month || '').trim();
  if (/^\d{6}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
  if (/^\d{4}-\d{2}$/.test(s)) return s;
  return '';
}

function formatTime(value?: string) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type ItemEditDraft = {
  specId: string;
  quantity: number;
  price: number;
  specName: string;
  unit: string;
};

function isUnlimitedPlanSpec(row: PurchasePlanGoodsRow) {
  return !(Number(row.spec_id) > 0);
}

function displayPlanSpecName(row: PurchasePlanGoodsRow) {
  if (isUnlimitedPlanSpec(row)) return UNLIMITED_SPEC_LABEL;
  return row.spec_name || row.spec?.name || '-';
}


type PlanAddTarget = {
  controlType: number;
  departmentId: number;
  accountId: number;
  accountName?: string;
};

type PlanAddBlockReason = 'exists' | 'conflict_unlimited' | 'conflict_spec';

/** 与后端 PurchasePlanService::planItemMatchKey 一致 */
function planItemMatchKey(
  goodsId: number,
  specId: number,
  accountId: number,
  withAccount: boolean,
): string {
  let key = '';
  if (specId > 0) key = `s:${specId}`;
  else if (goodsId > 0) key = `g:${goodsId}`;
  if (key && withAccount) key += `:a:${accountId}`;
  return key;
}

function scopePlanRows(rows: PurchasePlanGoodsRow[], target: PlanAddTarget): PurchasePlanGoodsRow[] {
  return rows.filter((row) => {
    if (Number(row.control_type) !== target.controlType) return false;
    if (
      (target.controlType === 2 || target.controlType === 3) &&
      target.departmentId > 0 &&
      Number(row.department_id) !== target.departmentId
    ) {
      return false;
    }
    if (target.controlType === 3 && target.accountId > 0) {
      return Number(row.account_id) === target.accountId;
    }
    return true;
  });
}

function checkPlanItemAddable(
  goodsId: number,
  specId: number,
  target: PlanAddTarget,
  rows: PurchasePlanGoodsRow[],
): PlanAddBlockReason | null {
  const withAccount = target.controlType === 3;
  const scoped = scopePlanRows(rows, target);
  const key = planItemMatchKey(goodsId, specId, target.accountId, withAccount);
  if (
    key &&
    scoped.some(
      (row) =>
        planItemMatchKey(
          Number(row.goods_id) || 0,
          Number(row.spec_id) || 0,
          Number(row.account_id) || 0,
          withAccount,
        ) === key,
    )
  ) {
    return 'exists';
  }
  const sameGoods = scoped.filter((row) => {
    if (Number(row.goods_id) !== goodsId) return false;
    if (withAccount && target.accountId > 0) {
      return Number(row.account_id) === target.accountId;
    }
    return true;
  });
  const hasUnlimited = sameGoods.some((row) => !(Number(row.spec_id) > 0));
  const hasSpec = sameGoods.some((row) => Number(row.spec_id) > 0);
  if (specId > 0 && hasUnlimited) return 'conflict_unlimited';
  if (!(specId > 0) && hasSpec) return 'conflict_spec';
  return null;
}

function planAddBlockMessage(reason: PlanAddBlockReason, name: string): string {
  if (reason === 'exists') return `「${name}」已在当前计划设置中，不能重复添加`;
  if (reason === 'conflict_unlimited') {
    return `「${name}」已设置为不限定规格，不能再添加限定规格`;
  }
  return `「${name}」已设置限定规格，不能再添加不限定规格`;
}

function pickPurchaseFilterCategories(list: CategoryListItem[]) {
  const top = list.filter((item) => !Number(item.pid));
  return top.length > 0 ? top : list;
}

function controlTypePlanTitle(group?: PurchasePlanItemGroup | null) {
  const text = String(group?.control_type_text || '').replace(/计划明细$/, '').trim();
  if (text) return `${text}计划明细`;
  const type = Number(group?.control_type);
  const fallback = type === 1 ? '全院限购' : type === 3 ? '个人限购' : type === 2 ? '科室限购' : '';
  return fallback ? `${fallback}计划明细` : '计划明细';
}

function resolvePlanAddTarget(
  flow: PurchasePlanRecord,
  group?: PurchasePlanItemGroup | null,
): PlanAddTarget | null {
  const items = Array.isArray(group?.items) ? group!.items! : [];
  const controlType = Number(group?.control_type) || (Number(flow.office?.id) > 0 ? 2 : 1);
  const departmentId =
    Number(flow.office?.id) || Number(items[0]?.department_id) || 0;
  let accountId = 0;
  let accountName = '';
  if (controlType === 3) {
    const first = items.find((row) => Number(row.account_id) > 0);
    accountId = Number(first?.account_id) || 0;
    accountName = String(first?.account_name || '');
    if (accountId < 1) return null;
  }
  if ((controlType === 2 || controlType === 3) && departmentId < 1) return null;
  return { controlType, departmentId, accountId, accountName };
}

function sumPlanRows(rows: PurchasePlanGoodsRow[]) {
  return rows.reduce(
    (acc, row) => {
      acc.planQty += Number(row.plan_quantity ?? 0) || 0;
      acc.planAmount += Number(row.plan_amount ?? 0) || 0;
      return acc;
    },
    { planQty: 0, planAmount: 0 },
  );
}

export default function PurchasePlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const flowId = String(id || '');
  const { refresh: refreshMember } = useMember();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [flow, setFlow] = useState<PurchasePlanRecord | null>(null);
  const [goods, setGoods] = useState<PurchasePlanGoodsRow[]>([]);
  const [groups, setGroups] = useState<PurchasePlanItemGroup[]>([]);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [modal, setModal] = useState<'approve' | 'cancel' | 'reverse' | 'delete' | null>(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [editUploadOpen, setEditUploadOpen] = useState(false);
  const [editUploadFile, setEditUploadFile] = useState<File | null>(null);
  const [editUploadError, setEditUploadError] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<ItemEditDraft | null>(null);
  const [savingItem, setSavingItem] = useState(false);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [showPasteCodes, setShowPasteCodes] = useState(false);
  const [addTarget, setAddTarget] = useState<PlanAddTarget | null>(null);
  const [addingItems, setAddingItems] = useState(false);
  const [pendingDeleteItem, setPendingDeleteItem] = useState<PurchasePlanGoodsRow | null>(null);
  const [purchaseCategories, setPurchaseCategories] = useState<CategoryListItem[]>([]);
  const [goodsCategoryId, setGoodsCategoryId] = useState('');
  const [categoriesReady, setCategoriesReady] = useState(false);
  const [goodsLoading, setGoodsLoading] = useState(false);
  const [hospitalPlanSource, setHospitalPlanSource] = useState<PurchasePlanGoodsRow | null>(null);
  const flowLoadedRef = useRef(false);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchDetail = useCallback(async () => {
    if (!flowId) {
      setLoadError('采购计划 ID 无效');
      setLoading(false);
      return;
    }
    if (!categoriesReady) return;
    const isFirstLoad = !flowLoadedRef.current;
    if (isFirstLoad) setLoading(true);
    else setGoodsLoading(true);
    setLoadError('');
    try {
      const res = await getPurchasePlanGoods(
        flowId,
        goodsCategoryId ? { category_id: goodsCategoryId } : {},
      );
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok || !res.data?.flow) {
        setFlow(null);
        setGoods([]);
        setGroups([]);
        setLoadError(res.message || res.msg || '采购计划加载失败');
        return;
      }
      setFlow(res.data.flow);
      const nextGoods = Array.isArray(res.data.goods) ? res.data.goods : [];
      const nextGroups = Array.isArray(res.data.groups) ? res.data.groups : [];
      setGoods(nextGoods);
      // 无 groups 时按扁平列表兜底归组（兼容旧接口）
      if (nextGroups.length > 0) {
        setGroups(nextGroups);
      } else if (nextGoods.length > 0) {
        const map = new Map<number, PurchasePlanGoodsRow[]>();
        nextGoods.forEach((row) => {
          const type = Number(row.control_type ?? 2) || 2;
          const list = map.get(type) || [];
          list.push(row);
          map.set(type, list);
        });
        setGroups(
          Array.from(map.entries()).map(([control_type, items]) => ({
            control_type,
            control_type_text: items[0]?.control_type_text || `类型${control_type}`,
            item_mode: items[0]?.item_mode || 'goods',
            item_count: items.length,
            items,
          })),
        );
      } else {
        setGroups([]);
      }
      flowLoadedRef.current = true;
    } catch {
      setFlow(null);
      setGoods([]);
      setGroups([]);
      setLoadError('采购计划加载失败，请稍后重试');
    } finally {
      setLoading(false);
      setGoodsLoading(false);
    }
  }, [flowId, goodsCategoryId, categoriesReady]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await getCategoryList();
        if (cancelled) return;
        const ok = res.error === false || Number(res.error) === 0;
        const list = pickPurchaseFilterCategories(Array.isArray(res.data) ? res.data : []);
        setPurchaseCategories(ok ? list : []);
        setGoodsCategoryId(ok && list[0] ? String(list[0].id) : '');
      } catch {
        if (!cancelled) {
          setPurchaseCategories([]);
          setGoodsCategoryId('');
        }
      } finally {
        if (!cancelled) setCategoriesReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    if (!flow) {
      clearDynamicBreadcrumbTitle();
      return;
    }
    const title =
      flow.apply_month_text && flow.office?.title
        ? `${flow.apply_month_text} [${flow.office.title}] 采购计划表`
        : flow.apply_month_text ||
          (flow.apply_month ? String(flow.apply_month) : '') ||
          '计划详情';
    setDynamicBreadcrumbTitle(title);
    return () => clearDynamicBreadcrumbTitle();
  }, [flow]);

  const approvalNodes = useMemo(
    () => (flow ? buildPurchaseApprovalNodes(flow) : []),
    [flow],
  );

  const categorySummary = Array.isArray(flow?.category_summary) ? flow!.category_summary! : [];
  const statusTag = flow
    ? getPurchaseStatusTag(flow.status, flow.status_text)
    : null;
  const canApprove = flow ? resolveCanApprove(flow) : false;
  const canCancel = flow ? resolveCanCancel(flow) : false;
  const canDelete = flow ? resolveCanDelete(flow) : false;
  const canReverse = flow ? resolveCanReverse(flow) : false;
  const canEdit = flow ? resolveCanEdit(flow) : false;
  const editMonth = flow ? applyMonthToValue(flow.apply_month) : '';
  const categoryTitleOptions = useMemo(
    () => purchaseCategories.map((item) => String(item.title || '').trim()).filter(Boolean),
    [purchaseCategories],
  );
  const selectedCategoryTitle =
    purchaseCategories.find((item) => String(item.id) === String(goodsCategoryId))?.title || '';

  const startEditItem = (row: PurchasePlanGoodsRow) => {
    const itemId = String(row.flow_item_id || '');
    if (!itemId || savingItem) return;
    const specId = Number(row.spec_id) > 0 ? String(row.spec_id) : '';
    setEditingItemId(itemId);
    setEditDraft({
      specId,
      quantity: Number(row.plan_quantity) || 1,
      price: Number(row.price) || 0,
      specName: specId ? row.spec_name || row.spec?.name || '' : UNLIMITED_SPEC_LABEL,
      unit: row.unit || row.spec?.unit || '',
    });
  };

  const cancelEditItem = () => {
    if (savingItem) return;
    setEditingItemId(null);
    setEditDraft(null);
  };

  const saveEditItem = async (row: PurchasePlanGoodsRow) => {
    if (!editingItemId || !editDraft || savingItem) return;
    const qty = Math.floor(Number(editDraft.quantity) || 0);
    if (qty < 1) {
      showToast('计划数须大于 0', 'error');
      return;
    }
    setSavingItem(true);
    try {
      const originalSpecId = Number(row.spec_id) > 0 ? String(row.spec_id) : '';
      const specChanged = editDraft.specId !== originalSpecId;
      const res = await updatePurchasePlanItem({
        id: editingItemId,
        quantity: qty,
        ...(specChanged
          ? {
              spec_id: editDraft.specId ? Number(editDraft.specId) : 0,
              goods_id: row.goods_id,
              price: editDraft.price,
            }
          : {}),
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '计划明细修改失败', 'error');
        return;
      }
      showToast(res.message || '计划明细已更新');
      setEditingItemId(null);
      setEditDraft(null);
      void fetchDetail();
    } catch {
      showToast('网络异常，修改失败', 'error');
    } finally {
      setSavingItem(false);
    }
  };

  const openAddGoods = (group?: PurchasePlanItemGroup | null) => {
    if (!flow || addingItems) return;
    const target = resolvePlanAddTarget(flow, group);
    if (!target) {
      showToast(
        Number(group?.control_type) === 3
          ? '个人限购缺少账号，无法添加物资'
          : '无法确定计划类型或科室，无法添加物资',
        'error',
      );
      return;
    }
    setAddTarget(target);
    setShowProductSelector(true);
  };

  const openPasteCodes = (group?: PurchasePlanItemGroup | null) => {
    if (!flow || addingItems) return;
    const target = resolvePlanAddTarget(flow, group);
    if (!target) {
      showToast(
        Number(group?.control_type) === 3
          ? '个人限购缺少账号，无法批量匹配'
          : '无法确定计划类型或科室，无法批量匹配',
        'error',
      );
      return;
    }
    setAddTarget(target);
    setShowPasteCodes(true);
  };

  const appendPlanItems = async (items: PurchaseSettingItem[], target: PlanAddTarget) => {
    if (!flow || addingItems || items.length === 0) return false;
    setAddingItems(true);
    try {
      const res = await createPurchasePlan({
        month: applyMonthToValue(flow.apply_month) || String(flow.apply_month || ''),
        control_type: target.controlType,
        department_id: target.controlType === 1 ? undefined : target.departmentId,
        account_id: target.controlType === 3 ? target.accountId : undefined,
        mode: 'append',
        items,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '添加物资失败', 'error');
        return false;
      }
      void fetchDetail();
      return true;
    } catch {
      showToast('网络异常，添加失败', 'error');
      return false;
    } finally {
      setAddingItems(false);
    }
  };

  const handleAddProduct = async (product: SelectableProduct) => {
    if (!addTarget) return;
    const goodsId = Number(product.goodsId) || 0;
    const specId = Number(product.id) || 0;
    const name = product.name || '未命名物资';
    const reason = checkPlanItemAddable(goodsId, specId, addTarget, goods);
    if (reason) {
      showToast(planAddBlockMessage(reason, name), 'error');
      return;
    }
    const qty = 1;
    const price = Number(product.price) || 0;
    const ok = await appendPlanItems(
      [
        {
          goods_id: goodsId,
          spec_id: specId > 0 ? specId : 0,
          quantity: qty,
          price,
          amount: Number((price * qty).toFixed(2)),
        },
      ],
      addTarget,
    );
    if (ok) {
      const suffix = addTarget.accountName ? `（${addTarget.accountName}）` : '';
      showToast(`已添加「${name}」${suffix}`);
    }
  };

  const handlePasteCodesConfirm = async (lines: PasteCodeLine[], _missed: PasteMissedItem[]) => {
    if (!addTarget) return;
    const settingMap = new Map<string, PurchaseSettingItem>();
    let skippedExist = 0;
    let skippedConflict = 0;
    let skippedUnmatched = 0;
    const pendingRows = [...goods];

    lines.forEach((line) => {
      const item = pasteLineToOrderItem(line);
      const goodsId = Number(item.productId) || 0;
      const specId = Number(item.specId) || 0;
      if (!goodsId) {
        skippedUnmatched += 1;
        return;
      }
      const reason = checkPlanItemAddable(goodsId, specId, addTarget, pendingRows);
      if (reason === 'exists') {
        skippedExist += 1;
        return;
      }
      if (reason) {
        skippedConflict += 1;
        return;
      }
      const key = planItemMatchKey(goodsId, specId, addTarget.accountId, addTarget.controlType === 3);
      const qty = Math.max(1, Math.floor(Number(item.quantity) || 1));
      const price = Number(item.unitPrice) || 0;
      const prev = settingMap.get(key);
      if (prev) {
        const nextQty = (Number(prev.quantity) || 0) + qty;
        settingMap.set(key, {
          ...prev,
          quantity: nextQty,
          amount: Number((price * nextQty).toFixed(2)),
        });
        return;
      }
      settingMap.set(key, {
        goods_id: goodsId,
        spec_id: specId > 0 ? specId : 0,
        quantity: qty,
        price,
        amount: Number((price * qty).toFixed(2)),
        remark: item.itemRemark?.trim() || undefined,
      });
      pendingRows.push({
        goods_id: goodsId,
        spec_id: specId,
        account_id: addTarget.accountId,
        department_id: addTarget.departmentId,
        control_type: addTarget.controlType,
      });
    });

    const payload = Array.from(settingMap.values());
    if (payload.length === 0) {
      const parts = [];
      if (skippedExist) parts.push(`${skippedExist} 项已存在`);
      if (skippedConflict) parts.push(`${skippedConflict} 项与现有规格设置冲突`);
      if (skippedUnmatched) parts.push(`${skippedUnmatched} 项未匹配`);
      showToast(parts.length ? `没有可添加的物资（${parts.join('，')}）` : '没有可添加的物资', 'error');
      return;
    }

    const ok = await appendPlanItems(payload, addTarget);
    if (!ok) return;
    const parts = [`已追加 ${payload.length} 项`];
    if (skippedExist) parts.push(`跳过已存在 ${skippedExist} 项`);
    if (skippedConflict) parts.push(`跳过冲突 ${skippedConflict} 项`);
    if (skippedUnmatched) parts.push(`未匹配 ${skippedUnmatched} 项`);
    showToast(parts.join('，'), skippedExist || skippedConflict || skippedUnmatched ? 'error' : 'success');
  };

  const existingAddSpecIds = useMemo(() => {
    if (!addTarget) return [];
    return scopePlanRows(goods, addTarget)
      .map((row) => String(row.spec_id || ''))
      .filter((id) => Number(id) > 0);
  }, [addTarget, goods]);

  const openEditUpload = () => {
    setEditUploadFile(null);
    setEditUploadError('');
    if (editFileInputRef.current) editFileInputRef.current.value = '';
    setEditUploadOpen(true);
  };

  const handleEditUploadSubmit = async () => {
    if (!flow || editSubmitting) return;
    if (!editUploadFile) {
      setEditUploadError('请上传采购清单模板');
      return;
    }
    const name = editUploadFile.name.toLowerCase();
    if (!/\.(xlsx|xls|csv)$/.test(name)) {
      setEditUploadError('仅支持 xlsx / xls / csv 文件');
      return;
    }
    setEditSubmitting(true);
    try {
      const res = await submitPurchasePlan({
        month: editMonth,
        file: editUploadFile,
        flow_id: flow.id,
        mode: 'overwrite',
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '采购计划修改失败', 'error');
        return;
      }
      showToast(res.message || '采购计划清单已覆盖更新');
      setEditUploadOpen(false);
      void fetchDetail();
    } catch {
      showToast('网络异常，提交失败', 'error');
    } finally {
      setEditSubmitting(false);
    }
  };

  const categoryTotals = useMemo(() => {
    return categorySummary.reduce(
      (acc, item) => {
        acc.qty += Number(item.plan_quantity ?? 0) || 0;
        acc.amount += Number(item.plan_amount ?? 0) || 0;
        return acc;
      },
      { qty: 0, amount: 0 },
    );
  }, [categorySummary]);

  const handleApproveSubmit = async (isAgree: boolean, remark: string) => {
    if (!flow) return;
    setActionSubmitting(true);
    try {
      const res = await approvePurchasePlan({
        flow_id: flow.id,
        is_agree: isAgree,
        remark,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '审批失败', 'error');
        return;
      }
      showToast(isAgree ? '已同意审批' : '已拒绝审批');
      setModal(null);
      void fetchDetail();
      void refreshMember({ force: true });
    } catch {
      showToast('审批失败，请稍后重试', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleCancelSubmit = async (remark: string) => {
    if (!flow) return;
    setActionSubmitting(true);
    try {
      const res = await cancelPurchasePlan({
        flow_id: flow.id,
        remark,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '取消失败', 'error');
        return;
      }
      showToast(res.message || '采购计划已取消');
      setModal(null);
      void fetchDetail();
      void refreshMember({ force: true });
    } catch {
      showToast('取消失败，请稍后重试', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleReverseConfirm = async () => {
    if (!flow) return;
    setActionSubmitting(true);
    try {
      const res = await unapprovePurchasePlan({
        flow_id: flow.id,
        remark: '反审退回',
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '反审失败', 'error');
        return;
      }
      showToast(res.message || '采购计划反审成功');
      setModal(null);
      void fetchDetail();
      void refreshMember({ force: true });
    } catch {
      showToast('反审失败，请稍后重试', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  const requestDeleteItem = (row: PurchasePlanGoodsRow) => {
    if (!row.flow_item_id || addingItems || savingItem) return;
    setPendingDeleteItem(row);
  };

  const handleDeleteItemConfirm = async () => {
    if (!pendingDeleteItem?.flow_item_id) return;
    setActionSubmitting(true);
    try {
      const res = await deletePurchasePlanItem(pendingDeleteItem.flow_item_id);
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '删除计划项目失败', 'error');
        return;
      }
      showToast(res.message || '计划项目已删除');
      setPendingDeleteItem(null);
      if (editingItemId && String(pendingDeleteItem.flow_item_id) === editingItemId) {
        setEditingItemId(null);
        setEditDraft(null);
      }
      void fetchDetail();
    } catch {
      showToast('网络异常，删除失败', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!flow) return;
    setActionSubmitting(true);
    try {
      const res = await deletePurchasePlan(flow.id);
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '删除失败', 'error');
        return;
      }
      showToast(res.message || '采购计划已删除');
      setModal(null);
      void refreshMember({ force: true });
      navigate('/purchase-plan');
    } catch {
      showToast('删除失败，请稍后重试', 'error');
    } finally {
      setActionSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 bg-background-100 flex items-center justify-center py-24">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400" />
          <p className="text-sm text-foreground-500 mt-3">正在加载计划详情...</p>
        </div>
      </div>
    );
  }

  if (loadError || !flow) {
    return (
      <div className="flex-1 bg-background-100">
        <div className="px-4 md:px-6 pt-0 pb-6 flex flex-col items-center justify-center py-24">
          <i className="ri-error-warning-line text-3xl text-primary-500 mb-3" />
          <p className="text-sm text-foreground-600 mb-4">{loadError || '采购计划不存在'}</p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void fetchDetail()}
              className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-700 hover:bg-background-50"
            >
              重新加载
            </button>
            <button
              type="button"
              onClick={() => navigate('/purchase-plan')}
              className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm hover:bg-primary-600"
            >
              返回列表
            </button>
          </div>
        </div>
      </div>
    );
  }

  const monthLabel =
    flow.apply_month_text || (flow.apply_month ? String(flow.apply_month) : '-');
  const departmentTitle = flow.office?.title || '-';
  const planTitle = `${monthLabel} [${departmentTitle}] 采购计划表`;

  return (
    <div className="flex-1 bg-background-100">
      {toast && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[130] px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
            toast.type === 'success'
              ? 'bg-accent-500 text-background-50'
              : 'bg-red-500 text-white'
          }`}
        >
          <i
            className={
              toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            }
          />
          {toast.message}
        </div>
      )}

      <div className="px-4 md:px-6 pt-0 pb-4 md:pb-6 space-y-4">
        <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
          <div className="px-4 md:px-5 py-3 border-b border-background-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex items-center gap-2 flex-wrap">
              <h1 className="text-base font-semibold text-foreground-900 truncate">
                {planTitle}
              </h1>
              {statusTag && (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-white shrink-0"
                  style={{ backgroundColor: statusTag.color }}
                >
                  <i className={`${statusTag.icon} text-xs`} />
                  {statusTag.label}
                </span>
              )}
            </div>

            <div className="inline-flex items-center justify-end gap-1.5 flex-wrap shrink-0">
              {canApprove && (
                <button
                  type="button"
                  onClick={() => setModal('approve')}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-primary-500 bg-primary-500 text-background-50 hover:bg-primary-600 hover:border-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-shield-check-line text-xs" />
                  审批
                </button>
              )}
              {canReverse && (
                <button
                  type="button"
                  onClick={() => setModal('reverse')}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-go-back-line text-xs" />
                  反审
                </button>
              )}
              {canCancel && (
                <button
                  type="button"
                  onClick={() => setModal('cancel')}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300 transition-colors whitespace-nowrap"
                >
                  <i className="ri-close-circle-line text-xs" />
                  取消
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  onClick={() => setModal('delete')}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border text-white transition-colors whitespace-nowrap hover:opacity-90"
                  style={{ backgroundColor: '#ed4014', borderColor: '#ed4014' }}
                >
                  <i className="ri-delete-bin-line text-xs" />
                  删除
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/purchase-plan/create')}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-accent-300 bg-accent-500 text-white hover:bg-accent-600 hover:border-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line text-xs" />
                创建计划
              </button>
              {canEdit && (
                <button
                  type="button"
                  onClick={openEditUpload}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-background-200 text-foreground-600 hover:bg-background-50 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-upload-2-line text-xs" />
                  重新上传
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/purchase-plan')}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-background-200 text-foreground-600 hover:bg-background-50 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line text-xs" />
                返回列表
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-background-200 p-4 lg:col-span-1">
            <h2 className="text-sm font-semibold text-foreground-900 mb-3">审批流程</h2>
            <div className="relative">
              <div className="absolute left-[7px] top-1.5 bottom-2 w-px bg-background-200" />
              {approvalNodes.map((node, nidx) => {
                const isLast = nidx === approvalNodes.length - 1;
                return (
                  <div key={node.id} className={`relative flex gap-2.5 ${isLast ? '' : 'pb-3'}`}>
                    <div className="relative z-10 shrink-0">
                      <div
                        className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${
                          node.dot === 'done'
                            ? 'bg-accent-500'
                            : node.dot === 'rejected'
                              ? 'bg-primary-500'
                              : node.dot === 'current'
                                ? 'bg-primary-500 ring-2 ring-primary-100'
                                : 'bg-background-100 border border-background-300'
                        }`}
                      >
                        {node.dot === 'done' && (
                          <i className="ri-check-line text-[9px] text-background-50" />
                        )}
                        {node.dot === 'rejected' && (
                          <i className="ri-close-line text-[9px] text-background-50" />
                        )}
                        {node.dot === 'pending' && (
                          <div className="w-1 h-1 rounded-full bg-background-400" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 -mt-px">
                      <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
                        <span
                          className={`text-[11px] leading-relaxed ${
                            node.dot === 'pending'
                              ? 'text-foreground-400'
                              : node.dot === 'rejected'
                                ? 'text-primary-700 font-medium'
                                : 'text-foreground-800 font-medium'
                          }`}
                        >
                          {node.title}
                        </span>
                        {node.subtitle && (
                          <span className="text-[11px] text-foreground-400">{node.subtitle}</span>
                        )}
                        {node.statusText && (
                          <span
                            className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                              node.statusTone === 'done'
                                ? 'text-accent-600'
                                : node.statusTone === 'rejected' || node.statusTone === 'current'
                                  ? 'text-primary-600'
                                  : 'text-foreground-400'
                            }`}
                          >
                            {node.statusTone === 'current' && (
                              <i className="ri-loader-4-line animate-spin text-[10px]" />
                            )}
                            {node.statusText}
                          </span>
                        )}
                      </div>
                      {node.time && (
                        <span className="text-[11px] text-foreground-400 block mt-0.5">
                          {formatTime(node.time)}
                        </span>
                      )}
                      {node.comment && (
                        <div className="mt-1.5 px-2 py-1 rounded bg-background-100 text-[11px] text-foreground-600">
                          <i className="ri-chat-quote-line text-foreground-400 mr-1" />
                          {node.comment}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-background-200 p-4 lg:col-span-2">
              <h2 className="text-sm font-semibold text-foreground-900 mb-3">分类合计</h2>
              {categorySummary.length === 0 ? (
                <p className="py-8 text-center text-sm text-foreground-400">暂无分类汇总</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-background-200">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-background-50 border-b border-background-200">
                        <th className="py-2 px-3 text-left font-semibold text-foreground-600">分类</th>
                        <th className="py-2 px-3 text-center font-semibold text-foreground-600">
                          计划数量
                        </th>
                        <th className="py-2 px-3 text-right font-semibold text-foreground-600">
                          计划金额
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {categorySummary.map((item, idx) => (
                        <tr
                          key={`${item.category_id ?? 'cat'}-${idx}`}
                          className="border-b border-background-100"
                        >
                          <td className="py-2 px-3 text-foreground-800">
                            {item.category_name || '未分类'}
                          </td>
                          <td className="py-2 px-3 text-center text-foreground-700">
                            {item.plan_quantity ?? 0}
                          </td>
                          <td className="py-2 px-3 text-right font-medium text-foreground-800">
                            ¥{Number(item.plan_amount ?? 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-background-50 border-t border-background-200">
                        <td className="py-2 px-3 text-xs font-semibold text-foreground-700">合计</td>
                        <td className="py-2 px-3 text-center text-xs font-bold text-foreground-900">
                          {categoryTotals.qty}
                        </td>
                        <td className="py-2 px-3 text-right text-xs font-bold text-primary-600">
                          ¥{categoryTotals.amount.toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
        </div>

        {groups.length === 0 ? (
          <div className="bg-white rounded-lg border border-background-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-background-100 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-foreground-900">计划明细</h2>
              {canEdit && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={addingItems}
                    onClick={() => openPasteCodes(null)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-300 text-xs text-foreground-700 hover:bg-background-50 disabled:opacity-50"
                  >
                    <i className="ri-clipboard-line" />
                    批量匹配
                  </button>
                  <button
                    type="button"
                    disabled={addingItems}
                    onClick={() => openAddGoods(null)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-xs disabled:opacity-50"
                  >
                    {addingItems ? (
                      <i className="ri-loader-4-line animate-spin" />
                    ) : (
                      <i className="ri-add-line" />
                    )}
                    添加物资
                  </button>
                </div>
              )}
            </div>
            <p className="py-12 text-center text-sm text-foreground-400">暂无计划明细</p>
          </div>
        ) : (
          <div className="space-y-4">
              {groups.map((group) => {
                const items = Array.isArray(group.items) ? group.items : [];
                const isConfig = String(group.item_mode || '') === 'config';
                const amountOnly = !!group.amount_only;
                const isHospitalLimitGroup = Number(group.control_type) === 1;
                const isDeptLimitGroup = Number(group.control_type) === 2;
                const showHospitalPlanAction = isHospitalLimitGroup || isDeptLimitGroup;
                const showRowActions = canEdit || showHospitalPlanAction;
                const fallback = sumPlanRows(items);
                const showTotals = {
                  planQty: group.plan_quantity != null ? Number(group.plan_quantity) || 0 : fallback.planQty,
                  planAmount: group.plan_amount != null ? Number(group.plan_amount) || 0 : fallback.planAmount,
                };

                return (
                  <div
                    key={`group-${group.control_type}`}
                    className="bg-white rounded-lg border border-background-200"
                  >
                    <div className="px-4 py-3 border-b border-background-100 flex items-center justify-between gap-3">
                      <div className="flex items-baseline gap-2 min-w-0">
                        <h2 className="text-sm font-semibold text-foreground-900">
                          {controlTypePlanTitle(group)}
                        </h2>
                      </div>
                      <div className="flex items-center gap-2">
                        {Number(group.control_type) === 1 && categoryTitleOptions.length > 0 && (
                          <div className="w-44 shrink-0">
                            <SearchableSelect
                              options={categoryTitleOptions}
                              value={selectedCategoryTitle}
                              onChange={(value) => {
                                const title = Array.isArray(value) ? value[0] || '' : value;
                                const hit = purchaseCategories.find(
                                  (item) => String(item.title || '').trim() === title,
                                );
                                if (hit) setGoodsCategoryId(String(hit.id));
                              }}
                              placeholder="采购分类"
                              allLabel="采购分类"
                              searchPlaceholder="搜索分类..."
                              icon="ri-price-tag-3-line"
                              clearable={false}
                            />
                          </div>
                        )}
                        {canEdit && !isConfig && (
                          <>
                          <button
                            type="button"
                            disabled={addingItems}
                            onClick={() => openPasteCodes(group)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-300 text-xs text-foreground-700 hover:bg-background-50 disabled:opacity-50"
                          >
                            <i className="ri-clipboard-line" />
                            批量匹配
                          </button>
                          <button
                            type="button"
                            disabled={addingItems}
                            onClick={() => openAddGoods(group)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-xs disabled:opacity-50"
                          >
                            {addingItems ? (
                              <i className="ri-loader-4-line animate-spin" />
                            ) : (
                              <i className="ri-add-line" />
                            )}
                            添加物资
                          </button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={`p-4 ${goodsLoading ? 'opacity-60 pointer-events-none' : ''}`}>
                    {items.length === 0 ? (
                      <p className="py-6 text-center text-xs text-foreground-400">本组暂无明细</p>
                    ) : isConfig ? (
                      <div className="overflow-x-auto rounded-lg border border-background-200">
                        <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                          <thead>
                            <tr className="bg-background-50">
                              <th className="py-2 px-2 text-center text-xs font-semibold text-foreground-600 w-10">
                                序号
                              </th>
                              <th className="py-2 px-3 text-left text-xs font-semibold text-foreground-600">
                                配置说明
                              </th>
                              <th className="py-2 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                分类
                              </th>
                              <th className="py-2 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                科室
                              </th>
                              {!amountOnly && (
                                <th className="py-2 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                  计划数
                                </th>
                              )}
                              <th className="py-2 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                计划金额
                              </th>
                              {canEdit && (
                                <th className="py-2 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap w-14">
                                  操作
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((g, idx) => (
                              <tr
                                key={String(g.flow_item_id ?? `cfg-${idx}`)}
                                className="hover:bg-background-50/60 transition-colors"
                              >
                                <td className="py-2 px-2 text-center text-foreground-500 text-xs">
                                  {idx + 1}
                                </td>
                                <td className="py-2 px-3 text-foreground-800 text-xs max-w-[220px]">
                                  <div className="font-medium truncate">
                                    {g.config_title || g.category_name || '-'}
                                  </div>
                                  {g.config_desc && (
                                    <div className="text-foreground-400 mt-0.5 truncate">
                                      {g.config_desc}
                                    </div>
                                  )}
                                  {g.remark && (
                                    <div className="text-foreground-400 mt-0.5 truncate">
                                      备注：{g.remark}
                                    </div>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-foreground-700 text-xs whitespace-nowrap">
                                  {g.category_name || '-'}
                                </td>
                                <td className="py-2 px-3 text-foreground-600 text-xs whitespace-nowrap">
                                  {Number(g.department_id) > 0 ? g.department_name || '-' : '全院'}
                                </td>
                                {!amountOnly && (
                                  <td className="py-2 px-3 text-center text-foreground-700 text-xs font-medium">
                                    {Number(g.plan_quantity ?? 0) || 0}
                                  </td>
                                )}
                                <td className="py-2 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">
                                  ¥{Number(g.plan_amount ?? 0).toFixed(2)}
                                </td>
                                {canEdit && (
                                  <td className="py-2 px-3 text-center">
                                    {g.flow_item_id ? (
                                      <button
                                        type="button"
                                        disabled={addingItems || savingItem || actionSubmitting}
                                        onClick={() => requestDeleteItem(g)}
                                        title="删除计划项目"
                                        className="inline-flex h-6 w-6 items-center justify-center rounded text-foreground-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                                      >
                                        <i className="ri-delete-bin-line text-sm" />
                                      </button>
                                    ) : null}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-background-50">
                              <td
                                colSpan={4}
                                className="py-2 px-3 text-right text-xs font-medium text-foreground-700"
                              >
                                小计
                              </td>
                              {!amountOnly && (
                                <td className="py-2 px-3 text-center text-xs font-bold text-foreground-900">
                                  {showTotals.planQty}
                                </td>
                              )}
                              <td className="py-2 px-3 text-right text-xs font-bold text-primary-600">
                                ¥{showTotals.planAmount.toFixed(2)}
                              </td>
                              {canEdit && <td />}
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-background-200">
                        <table className="w-full text-sm border-collapse [&_th]:border [&_th]:border-background-200 [&_td]:border [&_td]:border-background-100">
                          <thead>
                            <tr className="bg-background-50">
                              <th className="py-2.5 px-2 text-center text-xs font-semibold text-foreground-600 w-10 whitespace-nowrap">
                                序号
                              </th>
                              {Number(group.control_type) === 1 && (
                                <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                  分类
                                </th>
                              )}
                              {(Number(group.control_type) === 2 ||
                                Number(group.control_type) === 3) && (
                                <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                  科室名称
                                </th>
                              )}
                              {Number(group.control_type) === 3 && (
                                <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                  人员姓名
                                </th>
                              )}
                              <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                物资名称
                              </th>
                              <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                规格名称
                              </th>
                              <th className="py-2.5 px-3 text-left text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                编码
                              </th>
                              <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                单位
                              </th>
                              <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                计划数
                              </th>
                              <th className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                单价
                              </th>
                              <th className="py-2.5 px-3 text-right text-xs font-semibold text-foreground-600 whitespace-nowrap">
                                采购总价
                              </th>
                              {showRowActions && (
                                <th className="py-2.5 px-3 text-center text-xs font-semibold text-foreground-600 whitespace-nowrap w-28">
                                  操作
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((g, idx) => {
                              const itemId = String(g.flow_item_id || '');
                              const isRowEditing = canEdit && !!itemId && editingItemId === itemId;
                              const goodsId = String(g.goods_id || g.goods?.id || '');
                              return (
                              <tr
                                key={String(g.flow_item_id ?? `${g.spec_id}-${idx}`)}
                                className={`transition-colors ${
                                  isRowEditing
                                    ? 'bg-primary-50/50'
                                    : 'hover:bg-background-50/60'
                                }`}
                              >
                                <td className="py-2.5 px-2 text-center text-foreground-500 text-xs">
                                  {idx + 1}
                                </td>
                                {Number(group.control_type) === 1 && (
                                  <td className="py-2.5 px-3 text-foreground-700 text-xs whitespace-nowrap">
                                    {g.category_name || '-'}
                                  </td>
                                )}
                                {(Number(group.control_type) === 2 ||
                                  Number(group.control_type) === 3) && (
                                  <td className="py-2.5 px-3 text-foreground-700 text-xs whitespace-nowrap">
                                    {g.department_name || '-'}
                                  </td>
                                )}
                                {Number(group.control_type) === 3 && (
                                  <td className="py-2.5 px-3 text-foreground-700 text-xs whitespace-nowrap">
                                    {g.account_name || '-'}
                                  </td>
                                )}
                                <td className="py-2.5 px-3 text-foreground-800 text-xs max-w-[140px] truncate">
                                  {g.goods_name || g.goods?.title || '-'}
                                </td>
                                <td className="py-2.5 px-3 text-foreground-600 text-xs max-w-[180px]">
                                  {isRowEditing && editDraft && goodsId ? (
                                    <SpecCell
                                      productId={goodsId}
                                      specId={editDraft.specId}
                                      currentModel={editDraft.specName}
                                      allowUnlimitedSpec
                                      onChange={(specId, price, model, unit) => {
                                        setEditDraft((prev) =>
                                          prev
                                            ? {
                                                ...prev,
                                                specId,
                                                price,
                                                specName: model,
                                                unit,
                                              }
                                            : prev,
                                        );
                                      }}
                                    />
                                  ) : (
                                    <span
                                      className={`truncate block ${
                                        isUnlimitedPlanSpec(g) ||
                                        (isRowEditing && !editDraft?.specId)
                                          ? 'text-accent-700 font-medium'
                                          : ''
                                      }`}
                                    >
                                      {isRowEditing && editDraft
                                        ? editDraft.specName || UNLIMITED_SPEC_LABEL
                                        : displayPlanSpecName(g)}
                                    </span>
                                  )}
                                </td>
                                <td className="py-2.5 px-3 text-foreground-500 text-xs whitespace-nowrap">
                                  {g.code || g.spec?.sku || '-'}
                                </td>
                                <td className="py-2.5 px-3 text-center text-foreground-600 text-xs whitespace-nowrap">
                                  {isRowEditing && editDraft
                                    ? editDraft.unit || '-'
                                    : g.unit || g.spec?.unit || '-'}
                                </td>
                                <td className="py-2.5 px-3 text-center text-foreground-700 text-xs font-medium whitespace-nowrap">
                                  {isRowEditing && editDraft ? (
                                    <input
                                      type="number"
                                      min={1}
                                      max={99999}
                                      value={editDraft.quantity}
                                      disabled={savingItem}
                                      onChange={(e) => {
                                        const next = Number(e.target.value);
                                        setEditDraft((prev) =>
                                          prev
                                            ? { ...prev, quantity: Number.isNaN(next) ? 0 : next }
                                            : prev,
                                        );
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          void saveEditItem(g);
                                        } else if (e.key === 'Escape') {
                                          e.preventDefault();
                                          cancelEditItem();
                                        }
                                      }}
                                      className="w-16 mx-auto text-center text-xs border border-primary-300 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-primary-400"
                                    />
                                  ) : (
                                    Number(g.plan_quantity ?? 0) || 0
                                  )}
                                </td>
                                <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">
                                  ¥
                                  {(isRowEditing && editDraft
                                    ? editDraft.price
                                    : Number(g.price ?? 0)
                                  ).toFixed(2)}
                                </td>
                                <td className="py-2.5 px-3 text-right text-foreground-700 text-xs whitespace-nowrap">
                                  ¥{Number(g.plan_amount ?? 0).toFixed(2)}
                                </td>
                                {showRowActions && (
                                <td className="py-2.5 px-3 text-center">
                                  <div className="inline-flex items-center justify-center gap-1">
                                    {canEdit && itemId && (
                                      isRowEditing ? (
                                        <>
                                          <button
                                            type="button"
                                            disabled={savingItem}
                                            onClick={() => void saveEditItem(g)}
                                            title="保存"
                                            className="inline-flex h-6 w-6 items-center justify-center rounded text-primary-600 hover:bg-primary-50 disabled:opacity-50"
                                          >
                                            <i
                                              className={`text-sm ${
                                                savingItem
                                                  ? 'ri-loader-4-line animate-spin'
                                                  : 'ri-check-line'
                                              }`}
                                            />
                                          </button>
                                          <button
                                            type="button"
                                            disabled={savingItem}
                                            onClick={cancelEditItem}
                                            title="取消"
                                            className="inline-flex h-6 w-6 items-center justify-center rounded text-foreground-500 hover:bg-background-100 disabled:opacity-50"
                                          >
                                            <i className="ri-close-line text-sm" />
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            type="button"
                                            disabled={savingItem || addingItems}
                                            onClick={() => startEditItem(g)}
                                            title="修改规格和计划数"
                                            className="inline-flex h-6 w-6 items-center justify-center rounded text-foreground-400 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-50"
                                          >
                                            <i className="ri-edit-line text-sm" />
                                          </button>
                                          <button
                                            type="button"
                                            disabled={savingItem || addingItems || actionSubmitting}
                                            onClick={() => requestDeleteItem(g)}
                                            title="删除计划项目"
                                            className="inline-flex h-6 w-6 items-center justify-center rounded text-foreground-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                                          >
                                            <i className="ri-delete-bin-line text-sm" />
                                          </button>
                                        </>
                                      )
                                    )}
                                    {showHospitalPlanAction && !isRowEditing && itemId && (
                                      <button
                                        type="button"
                                        disabled={savingItem || addingItems}
                                        onClick={() => setHospitalPlanSource(g)}
                                        title="全院科室计划明细"
                                        className="inline-flex items-center gap-0.5 h-6 px-1.5 rounded text-foreground-400 hover:text-primary-600 hover:bg-primary-50 disabled:opacity-50 text-xs whitespace-nowrap"
                                      >
                                        <i className="ri-file-list-3-line text-sm" />
                                        计划明细
                                      </button>
                                    )}
                                  </div>
                                </td>
                                )}
                              </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="bg-background-50">
                              <td
                                colSpan={Number(group.control_type) === 3 ? 7 : 6}
                                className="py-2.5 px-3 text-right text-xs font-medium text-foreground-700"
                              >
                                小计
                              </td>
                              <td className="py-2.5 px-3 text-center text-xs font-bold text-foreground-900">
                                {showTotals.planQty}
                              </td>
                              <td />
                              <td className="py-2.5 px-3 text-right text-xs font-bold text-primary-600">
                                ¥{showTotals.planAmount.toFixed(2)}
                              </td>
                              {showRowActions && <td />}
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      <HospitalPlanItemsModal
        isOpen={!!hospitalPlanSource}
        source={hospitalPlanSource}
        onClose={() => setHospitalPlanSource(null)}
      />

      <ApproveModal
        isOpen={modal === 'approve'}
        monthLabel={monthLabel}
        department={flow.office?.title || ''}
        submitter={flow.member?.nickname || flow.member?.account || ''}
        createdAt={flow.created_at || ''}
        categories={categorySummary.map((item) => ({
          name: item.category_name || '未分类',
          planQuantity: Number(item.plan_quantity ?? 0) || 0,
          planAmount: Number(item.plan_amount ?? 0) || 0,
        }))}
        submitting={actionSubmitting}
        onCancel={() => {
          if (!actionSubmitting) setModal(null);
        }}
        onApprove={(remark) => void handleApproveSubmit(true, remark)}
        onReject={(remark) => void handleApproveSubmit(false, remark)}
      />

      <CancelModal
        isOpen={modal === 'cancel'}
        monthLabel={monthLabel}
        submitting={actionSubmitting}
        onCancel={() => {
          if (!actionSubmitting) setModal(null);
        }}
        onConfirm={handleCancelSubmit}
      />

      <ConfirmModal
        isOpen={modal === 'reverse'}
        title="反审确认"
        message={`确认对 ${monthLabel || '该'} 采购计划执行反审？反审后将回到审批中，可重新审批。`}
        confirmLabel="确认反审"
        danger
        loading={actionSubmitting}
        onCancel={() => {
          if (!actionSubmitting) setModal(null);
        }}
        onConfirm={() => void handleReverseConfirm()}
      />

      <ConfirmModal
        isOpen={modal === 'delete'}
        title="删除采购计划"
        message={`确认删除 ${monthLabel || '该'} 采购计划？删除后不可恢复，相关明细将一并清除。`}
        confirmLabel="确认删除"
        danger
        loading={actionSubmitting}
        onCancel={() => {
          if (!actionSubmitting) setModal(null);
        }}
        onConfirm={() => void handleDeleteConfirm()}
      />

      <ConfirmModal
        isOpen={!!pendingDeleteItem}
        title="删除计划项目"
        message={`确认删除「${
          pendingDeleteItem?.goods_name ||
          pendingDeleteItem?.goods?.title ||
          pendingDeleteItem?.config_title ||
          '该'
        }」的计划项目？删除后不可恢复。`}
        confirmLabel="确认删除"
        danger
        loading={actionSubmitting}
        onCancel={() => {
          if (!actionSubmitting) setPendingDeleteItem(null);
        }}
        onConfirm={() => void handleDeleteItemConfirm()}
      />

      {editUploadOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4">
          <div className="bg-background-50 rounded-xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
              <div>
                <h3 className="text-sm font-semibold text-foreground-900">修改计划表</h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  上传新清单将覆盖当前审批中的采购计划物资
                </p>
              </div>
              <button
                type="button"
                disabled={editSubmitting}
                onClick={() => setEditUploadOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 disabled:opacity-50"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground-800 mb-1.5 block">
                  <span className="text-red-500 mr-0.5">*</span>
                  计划月份
                </label>
                <MonthPicker value={editMonth} onChange={() => undefined} disabled />
              </div>
              <div>
                <label
                  className={`text-sm font-medium mb-1.5 block ${
                    editUploadError ? 'text-red-600' : 'text-foreground-800'
                  }`}
                >
                  <span className="text-red-500 mr-0.5">*</span>
                  采购清单模板
                </label>
                <input
                  ref={editFileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={(e) => {
                    setEditUploadFile(e.target.files?.[0] || null);
                    setEditUploadError('');
                  }}
                />
                <div
                  className={`rounded-lg border border-dashed px-4 py-4 ${
                    editUploadError
                      ? 'border-red-500 ring-1 ring-red-200 bg-red-50'
                      : 'border-background-300 bg-background-50'
                  }`}
                >
                  {editUploadFile ? (
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm text-foreground-800 truncate">{editUploadFile.name}</p>
                      <button
                        type="button"
                        disabled={editSubmitting}
                        onClick={() => {
                          setEditUploadFile(null);
                          if (editFileInputRef.current) editFileInputRef.current.value = '';
                        }}
                        className="text-xs text-foreground-500 hover:text-red-600"
                      >
                        移除
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => editFileInputRef.current?.click()}
                      className="w-full text-center py-2"
                    >
                      <i
                        className={`ri-upload-cloud-2-line text-2xl ${
                          editUploadError ? 'text-red-400' : 'text-foreground-300'
                        }`}
                      />
                      <p
                        className={`text-sm mt-1 ${
                          editUploadError ? 'text-red-600' : 'text-foreground-600'
                        }`}
                      >
                        点击上传采购清单模板
                      </p>
                    </button>
                  )}
                </div>
                {editUploadError && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1 font-medium">
                    <i className="ri-error-warning-line" />
                    {editUploadError}
                  </p>
                )}
              </div>
            </div>
            <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={editSubmitting}
                onClick={() => setEditUploadOpen(false)}
                className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                disabled={editSubmitting}
                onClick={() => void handleEditUploadSubmit()}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium disabled:opacity-50"
              >
                {editSubmitting ? (
                  <i className="ri-loader-4-line animate-spin" />
                ) : (
                  <i className="ri-send-plane-line" />
                )}
                {editSubmitting ? '提交中...' : '确认覆盖'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ProductSelector
        isOpen={showProductSelector}
        onClose={() => setShowProductSelector(false)}
        onSelect={(product) => void handleAddProduct(product)}
        existingProductIds={existingAddSpecIds}
      />

      <PasteCodesModal
        isOpen={showPasteCodes}
        onClose={() => setShowPasteCodes(false)}
        onConfirm={(lines, missed) => void handlePasteCodesConfirm(lines, missed)}
        allowUnlimitedSpec
      />
    </div>
  );
}
